import React from 'react';
import { Modal } from 'antd';
import { hasFeatureInLocalStorage } from '../../../global/featureFlags';
import { captureAnalytics } from '../../../actions/preload';

export const SCHEDULED_APPOINTMENT_LOOKUP_FEATURE = 'referral_scheduled_appointment_lookup';

// Moving a referral into Scheduled reads its appointment from ModMed before the
// status saves (the server waits up to 5 seconds), so staff see why it takes a moment.
export const looksUpScheduledAppointment = (status, currentStatusId) => (
  status?.name === 'Scheduled'
  && status.id !== currentStatusId
  && hasFeatureInLocalStorage(SCHEDULED_APPOINTMENT_LOOKUP_FEATURE)
);

export const describeAppointmentLookup = (lookup) => {
  switch (lookup?.outcome) {
    case 'found': {
      const { display_time: displayTime, provider_name: providerName, location } = lookup.appointment;
      return [displayTime, providerName, location].filter(Boolean).join(' · ');
    }
    case 'not_found':
      return 'No appointment in ModMed yet.';
    case 'no_chart':
      return 'No ModMed chart is linked to this referral, so no appointment was pulled.';
    case 'unavailable':
      return 'ModMed did not answer in time. We will check again shortly and fill the appointment in.';
    default:
      return null;
  }
};

// Runs save() (the status update) behind a modal that reports the appointment the
// server pulled. Without the lookup, save() runs as before.
export const saveWithAppointmentLookup = async ({ status, currentStatusId, save }) => {
  if (!looksUpScheduledAppointment(status, currentStatusId)) return save();

  const modal = Modal.info({
    title: 'Moving to Scheduled',
    content: 'Pulling appointment details from ModMed…',
    okText: 'Close',
    okButtonProps: { loading: true },
    keyboard: false,
  });
  let response;
  try {
    response = await save();
  } catch (error) {
    modal.destroy();
    throw error;
  }

  const description = describeAppointmentLookup(response?.data?.appointment_lookup);
  if (response?.error || !description) {
    modal.destroy();
    return response;
  }

  const { outcome } = response.data.appointment_lookup;
  captureAnalytics('scheduled_appointment_lookup_result', { outcome });
  modal.update({
    title: outcome === 'found' ? 'Appointment pulled from ModMed' : 'Moved to Scheduled',
    content: <span data-testid="appointment-lookup-result">{description}</span>,
    okButtonProps: { loading: false },
    keyboard: true,
    onOk: () => captureAnalytics('scheduled_appointment_lookup_closed', { outcome }),
  });
  return response;
};
